'use client';
import React from 'react';
import { CheckCircle2, Circle, Clock } from 'lucide-react';
import { INSPECTION_CATEGORIES } from './inspectionData';

interface Step {
  id: string;
  label: string;
}

interface Props {
  currentStep: number;
  steps: Step[];
  categories: typeof INSPECTION_CATEGORIES;
  getCategoryCompletion: (code: string) => { answered: number; total: number };
  onStepClick: (index: number) => void;
  inspectionType: string;
}

export default function InspectionProgressSidebar({ currentStep, steps, categories, getCategoryCompletion, onStepClick, inspectionType }: Props) {
  return (
    <div className="hidden lg:flex flex-col w-60 xl:w-64 flex-shrink-0 border-r bg-white h-screen sticky top-0 overflow-y-auto scrollbar-thin"
      style={{ borderColor: 'hsl(var(--border))' }}>
      <div className="p-4 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted">Inspection Progress</p>
        <p className="text-xs text-muted mt-0.5">{inspectionType} Inspection</p>
      </div>
      <div className="p-3 space-y-0.5">
        {/* Vessel step */}
        <button
          onClick={() => onStepClick(0)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-all duration-150 ${currentStep === 0 ? 'font-semibold' : 'font-medium'}`}
          style={{
            background: currentStep === 0 ? 'hsl(218 70% 22% / 0.1)' : 'transparent',
            color: currentStep === 0 ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
          }}
        >
          {currentStep > 0 ? (
            <CheckCircle2 size={14} style={{ color: 'hsl(var(--success))', flexShrink: 0 }} />
          ) : (
            <Circle size={14} style={{ flexShrink: 0 }} />
          )}
          <span className="truncate">Vessel Details</span>
        </button>

        <div className="pt-1 pb-0.5 px-3">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'hsl(var(--muted-foreground))' }}>
            Inspection Categories
          </p>
        </div>

        {categories.map((cat, i) => {
          const stepIndex = i + 1;
          const { answered, total } = getCategoryCompletion(cat.code);
          const isActive = currentStep === stepIndex;
          const isComplete = answered === total && total > 0;
          const isStarted = answered > 0 && !isComplete;
          const isPast = currentStep > stepIndex;

          return (
            <button
              key={`progress-${cat.code}`}
              onClick={() => onStepClick(stepIndex)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-all duration-150"
              style={{
                background: isActive ? 'hsl(218 70% 22% / 0.1)' : 'transparent',
                color: isActive ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
              }}
            >
              {isComplete || (isPast && !isStarted) ? (
                <CheckCircle2 size={13} style={{ color: 'hsl(var(--success))', flexShrink: 0 }} />
              ) : isStarted ? (
                <Clock size={13} style={{ color: 'hsl(var(--warning))', flexShrink: 0 }} />
              ) : (
                <Circle size={13} style={{ flexShrink: 0 }} />
              )}
              <span className="flex-1 truncate text-xs font-medium">{cat.shortLabel}</span>
              {(isStarted || isComplete) && (
                <span className="text-xs tabular-nums" style={{ color: isComplete ? 'hsl(var(--success))' : 'hsl(var(--warning))' }}>
                  {answered}/{total}
                </span>
              )}
            </button>
          );
        })}

        {/* Review step */}
        <button
          onClick={() => onStepClick(steps.length - 1)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-all duration-150 mt-1"
          style={{
            background: currentStep === steps.length - 1 ? 'hsl(218 70% 22% / 0.1)' : 'transparent',
            color: currentStep === steps.length - 1 ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
          }}
        >
          <Circle size={14} style={{ flexShrink: 0 }} />
          <span className="truncate font-medium text-xs">Review & Submit</span>
        </button>
      </div>
    </div>
  );
}